import moment from "moment";
import React, { useState } from "react";
import { useFetch } from "../Hooks/useFetch";
import { FetchBoundary } from "../common/FetchBoundary";
import { withTheme } from "../common/ThemeProviderWrapper";
import { Tab, Tabs } from "@mui/material";
import { humanizeText } from "../utils/StringFormatters";
import { wholeNumberWithCommas } from "../utils/NumberFormatter";
import { ClaimsActivityBarChart } from "./ClaimsActivityBarChart";
import { ChevronRight } from "@material-ui/icons";
import DataFormatter from "../Submissions/DataFormatter";

const ClaimsActivityCard = ({
  organizationId,
  medicalSubmissionPath,
  pharmacySubmissionPath,
  medicalEncounterSubmissionPath,
  pharmacyEncounterSubmissionPath,
}) => {
  const [tab, setTab] = useState("pharmacy");
  const [claimsCacheByTab, setClaimsCacheByTab] = useState({});
  const cachedClaimsData = claimsCacheByTab[tab];

  const claimsFetcher = useFetch({
    url: cachedClaimsData
      ? undefined
      : `/api/organization/${organizationId}/dashboard/claims?type=${tab}`,
    onSuccess: (data) => setClaimsCacheByTab((prev) => ({ ...prev, [tab]: data })),
  });

  const claimsResponse = cachedClaimsData ?? claimsFetcher.data;

  const submissions = claimsResponse?.submissions ?? [];
  const monthlyConformanceCharts = claimsResponse?.monthly_conformance_charts ?? [];
  const claimsData = claimsResponse?.claims ?? {};
  const claimCount = claimsData.count ?? 0;

  const conformingCount = claimsData.conforming_count ?? 0;
  const conformingPercent = claimsData.conforming_percent ?? 0;

  const nonconformingCount = claimsData.non_conforming_count ?? 0;
  const nonconformingPercent = claimsData.non_conforming_percent ?? 0;

  const submissionPathsByTab = {
    pharmacy: pharmacySubmissionPath,
    pharmacy_encounter: pharmacyEncounterSubmissionPath,
    medical: medicalSubmissionPath,
    medical_encounter: medicalEncounterSubmissionPath,
  };

  return (
    <div className="div6">
      <div className="tile w--full">
        <div className="tile__header tile__header--spread p-0">
          <div className="tile__header__details">
            <div className="tile__header__details__title">Claims Activity</div>
            <div className="tile__header__details__subtitle">
              Updated - {moment().format("MM-DD-YYYY")}
            </div>
          </div>
        </div>

        <div className="w--full my-4 border--bottom tab-border-bottom">
          <Tabs value={tab} onChange={(e, newValue) => setTab(newValue)}>
            <Tab label={<div className="flex align-items-center">Pharmacy</div>} value="pharmacy" />
            <Tab
              label={<div className="flex align-items-center">Pharmacy Encounter</div>}
              value="pharmacy_encounter"
            />
            <Tab label={<div className="flex align-items-center">Medical</div>} value="medical" />
            <Tab
              label={<div className="flex align-items-center">Medical Encounter</div>}
              value="medical_encounter"
            />
          </Tabs>
        </div>
        <FetchBoundary {...claimsFetcher} style={{ height: 556 }}>
          <div className="flex align-items-center gap--8 mb-2">
            <div className="t--xl t--500">{wholeNumberWithCommas(claimCount)}</div>{" "}
            <div className="t--secondary t--sm t--500">Total {humanizeText(tab)} Claims</div>
          </div>
          <div className="t--sm t--secondary mb-4">
            For the month of {moment().format("MMMM")},{" "}
            <span className="t--500">{wholeNumberWithCommas(conformingCount)}</span> or{" "}
            <span className="t--500">{conformingPercent}%</span> of your claims are Conforming and{" "}
            <span className="t--500">{wholeNumberWithCommas(nonconformingCount)}</span> or{" "}
            <span className="t--500">{nonconformingPercent}%</span> are Non-Conforming. Continue
            submitting claims to maintain access to pricing.
          </div>
          <div style={{ height: "260px" }}>
            <ClaimsActivityBarChart data={monthlyConformanceCharts} />
          </div>
          <div style={{ overflowY: "auto", height: 210 }}>
            <table className="primary header-light-navy" style={{ borderCollapse: "collapse" }}>
              <thead className="h--40">
                <tr className="sm">
                  <th className="t--transform--none">Date</th>
                  <th className="t--transform--none">Row Count</th>
                  <th className="t--transform--none">% Conforming</th>
                  <th></th>
                </tr>
              </thead>

              <tbody>
                {submissions
                  .sort(
                    (a, b) => new Date(b.last_submission_date) - new Date(a.last_submission_date)
                  )
                  .map((submission, index) => (
                    <tr className="sm t--sm border--bottom" key={index}>
                      <td>{DataFormatter.formatDate(submission.last_submission_date)}</td>
                      <td>{wholeNumberWithCommas(submission.row_count)}</td>
                      <td>{submission.conforming_percent}%</td>
                      <td>
                        <div className="flex align-items-center justify-content-end">
                          <a href={submissionPathsByTab[tab]}>
                            <button className="button button--white w--32 h--32">
                              <ChevronRight fontSize="small" />
                            </button>
                          </a>
                        </div>
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
        </FetchBoundary>
      </div>
    </div>
  );
};

export default withTheme(ClaimsActivityCard);
